import {
  Sponsor,
  SponsorActivity,
  SponsorBrand,
  SponsorContact,
  SponsorEstimatedMetrics,
  SponsorFee,
  SponsorObservedMetrics,
  SponsorOpportunity,
  SponsorOpportunityStage,
  SponsorOperationsState,
  SponsorReport,
} from '../types';

const EMPTY_SPONSOR_OPERATIONS: SponsorOperationsState = {
  version: 1,
  dataSource: 'provider',
  brands: [],
  contacts: [],
  opportunities: [],
  inventory: [],
  activations: [],
  activities: [],
  reports: [],
  renewals: [],
  objections: [],
  updatedAt: new Date(0).toISOString(),
};

const LEGACY_STAGE_MAP: Record<Sponsor['status'], SponsorOpportunityStage> = {
  prospect: 'prospect', contacted: 'contacted', meeting_scheduled: 'meeting', proposal_sent: 'proposal',
  negotiation: 'negotiation', confirmed: 'closed_won', paid: 'active', declined: 'lost',
};

function migrateLegacySponsors(legacySponsors: Sponsor[]) {
  const fallbackTimestamp = new Date().toISOString();
  const brands = legacySponsors.map((sponsor): SponsorBrand => ({
    id: sponsor.id,
    brandName: sponsor.companyName,
    companyName: sponsor.companyName,
    industry: sponsor.industry || 'Sin clasificar',
    category: sponsor.tier,
    website: sponsor.websiteUrl,
    country: 'Sin especificar',
    city: 'Sin especificar',
    priority: sponsor.tier === 'title' || sponsor.tier === 'platinum' ? 'high' : sponsor.tier === 'community' ? 'low' : 'medium',
    fitScore: sponsor.status === 'confirmed' || sponsor.status === 'paid' ? 85 : 60,
    fitReason: 'Migrado desde Sponsors clásico. Revisar afinidad con información propia antes de compartirla.',
    relationshipType: sponsor.status === 'confirmed' || sponsor.status === 'paid' ? 'previous_sponsor' : 'existing_relationship',
    owner: sponsor.assignedOwner || 'Sin asignar',
    tags: [sponsor.tier, 'migrado'],
    notes: sponsor.notes || '',
    legacySponsorId: sponsor.id,
    createdAt: sponsor.lastActivityDate ? `${sponsor.lastActivityDate}T12:00:00.000Z` : fallbackTimestamp,
    updatedAt: sponsor.lastActivityDate ? `${sponsor.lastActivityDate}T12:00:00.000Z` : fallbackTimestamp,
  }));
  const contacts = legacySponsors.map((sponsor): SponsorContact => ({
    id: `contact-${sponsor.id}`,
    brandId: sponsor.id,
    name: sponsor.contactName,
    role: 'Contacto principal',
    department: 'Partnerships',
    email: sponsor.contactEmail,
    phone: sponsor.contactPhone,
    relationshipStrength: sponsor.status === 'confirmed' || sponsor.status === 'paid' ? 'strong' : 'warm',
    source: 'Sponsors clásico',
    lastContactAt: sponsor.lastActivityDate,
    notes: '',
  }));
  const opportunities = legacySponsors.map((sponsor): SponsorOpportunity => {
    const stage = sponsor.status === 'paid' || (sponsor.status === 'confirmed' && sponsor.invoiceStatus === 'paid')
      ? 'active'
      : LEGACY_STAGE_MAP[sponsor.status];
    return {
      id: `opportunity-${sponsor.id}`,
      title: `${sponsor.companyName} × Patrocinio actual`,
      brandId: sponsor.id,
      projectName: 'Proyecto actual',
      inventorySelections: [],
      contactIds: [`contact-${sponsor.id}`],
      stage,
      owner: sponsor.assignedOwner || 'Sin asignar',
      basePrice: sponsor.amount,
      markup: 0,
      finalPrice: sponsor.amount,
      currency: 'USD',
      grosymFee: { mode: 'fixed', value: 0, calculatedAmount: 0 },
      referralFee: 0,
      monthlyValue: sponsor.amount,
      contractValue: sponsor.amount,
      durationMonths: 1,
      probability: stage === 'active' ? 100 : stage === 'closed_won' ? 95 : stage === 'lost' ? 0 : 50,
      exclusivity: sponsor.tier === 'title',
      nextAction: stage === 'active' ? 'Preparar reporte y renovación' : stage === 'lost' ? 'Revisar en próximo ciclo' : 'Definir próximo seguimiento',
      nextActionDate: stage === 'active' ? new Date().toISOString().slice(0, 10) : undefined,
      source: 'Migración Sponsors clásico',
      notes: sponsor.notes || '',
      createdAt: sponsor.lastActivityDate ? `${sponsor.lastActivityDate}T12:00:00.000Z` : fallbackTimestamp,
      updatedAt: sponsor.lastActivityDate ? `${sponsor.lastActivityDate}T12:00:00.000Z` : fallbackTimestamp,
    };
  });
  const activities = legacySponsors.map((sponsor): SponsorActivity => ({
    id: `activity-${sponsor.id}`,
    opportunityId: `opportunity-${sponsor.id}`,
    type: 'note',
    title: 'Registro migrado',
    description: 'Sponsor importado desde la versión anterior sin perder su estado ni valor.',
    date: sponsor.lastActivityDate || new Date().toISOString().slice(0, 10),
    owner: sponsor.assignedOwner || 'Sistema',
  }));
  return { brands, contacts, opportunities, activities };
}

export const SPONSOR_STAGE_ORDER: SponsorOpportunity['stage'][] = [
  'prospect', 'contacted', 'replied', 'meeting', 'proposal', 'negotiation', 'closed_won', 'active', 'renewal', 'lost',
];

export const SPONSOR_STAGE_LABELS: Record<SponsorOpportunity['stage'], string> = {
  prospect: 'Prospect', contacted: 'Contacted', replied: 'Replied', meeting: 'Meeting', proposal: 'Proposal',
  negotiation: 'Negotiation', closed_won: 'Closed Won', active: 'Active', renewal: 'Renewal', lost: 'Lost',
};

export interface OpportunityFinancialInput {
  basePrice: number;
  markup: number;
  durationMonths: number;
  grosymFeeMode: SponsorFee['mode'];
  grosymFeeValue: number;
}

export function calculateOpportunityFinancials(input: OpportunityFinancialInput) {
  const basePrice = Math.max(0, input.basePrice || 0);
  const markup = Math.max(0, input.markup || 0);
  const durationMonths = Math.max(1, input.durationMonths || 1);
  const finalPrice = basePrice + markup;
  const contractValue = finalPrice * durationMonths;
  const grosymRevenue = input.grosymFeeMode === 'percentage'
    ? contractValue * (Math.max(0, input.grosymFeeValue) / 100)
    : input.grosymFeeMode === 'markup'
      ? markup
      : Math.max(0, input.grosymFeeValue || 0);

  return { basePrice, markup, finalPrice, monthlyValue: finalPrice, contractValue, durationMonths, grosymRevenue };
}

export interface SponsorOverviewMetrics {
  pipelineValue: number;
  closedValue: number;
  activeSponsors: number;
  openOpportunities: number;
  proposalsSent: number;
  upcomingMeetings: number;
  activeActivations: number;
  renewalsDue: number;
}

export function calculateSponsorOverview(state: SponsorOperationsState, referenceDate = new Date()): SponsorOverviewMetrics {
  const terminalStages = new Set<SponsorOpportunity['stage']>(['lost']);
  const closedStages = new Set<SponsorOpportunity['stage']>(['closed_won', 'active', 'renewal']);
  const today = referenceDate.toISOString().slice(0, 10);
  const inFourteenDays = new Date(referenceDate);
  inFourteenDays.setDate(inFourteenDays.getDate() + 14);
  const meetingLimit = inFourteenDays.toISOString().slice(0, 10);

  return {
    pipelineValue: state.opportunities.filter(item => !terminalStages.has(item.stage)).reduce((sum, item) => sum + item.contractValue, 0),
    closedValue: state.opportunities.filter(item => closedStages.has(item.stage)).reduce((sum, item) => sum + item.contractValue, 0),
    activeSponsors: new Set(state.opportunities.filter(item => item.stage === 'active').map(item => item.brandId)).size,
    openOpportunities: state.opportunities.filter(item => !['closed_won', 'active', 'lost'].includes(item.stage)).length,
    proposalsSent: state.opportunities.filter(item => ['proposal', 'negotiation'].includes(item.stage)).length,
    upcomingMeetings: state.opportunities.filter(item => item.stage === 'meeting' && item.nextActionDate && item.nextActionDate >= today && item.nextActionDate <= meetingLimit).length,
    activeActivations: state.activations.filter(item => ['approved', 'scheduled', 'live'].includes(item.status)).length,
    renewalsDue: state.opportunities.filter(item => item.stage === 'active' && getRenewalDaysRemaining(item, referenceDate) !== null && getRenewalDaysRemaining(item, referenceDate)! <= 90).length,
  };
}

export function getRenewalDaysRemaining(opportunity: SponsorOpportunity, referenceDate = new Date()): number | null {
  if (!opportunity.endDate) return null;
  const end = new Date(`${opportunity.endDate}T23:59:59`);
  if (Number.isNaN(end.getTime())) return null;
  return Math.ceil((end.getTime() - referenceDate.getTime()) / 86_400_000);
}

export function getDaysSinceActivity(opportunityId: string, activities: SponsorActivity[], referenceDate = new Date()): number | null {
  const latest = activities.filter(item => item.opportunityId === opportunityId).map(item => item.date).sort().at(-1);
  if (!latest) return null;
  return Math.max(0, Math.floor((referenceDate.getTime() - new Date(`${latest}T12:00:00`).getTime()) / 86_400_000));
}

export function getNextActionWarnings(state: SponsorOperationsState, referenceDate = new Date()) {
  const today = referenceDate.toISOString().slice(0, 10);
  return state.opportunities
    .filter(item => !['lost', 'closed_won'].includes(item.stage))
    .map(item => {
      const daysInactive = getDaysSinceActivity(item.id, state.activities, referenceDate);
      const renewalDays = item.stage === 'active' ? getRenewalDaysRemaining(item, referenceDate) : null;
      if (!item.nextAction.trim()) return { opportunity: item, kind: 'missing' as const, label: 'No next action assigned' };
      if (item.nextActionDate && item.nextActionDate < today) return { opportunity: item, kind: 'overdue' as const, label: 'Follow-up overdue' };
      if (renewalDays !== null && renewalDays <= 90) return { opportunity: item, kind: 'renewal' as const, label: `Renewal due in ${renewalDays} days` };
      if (daysInactive !== null && daysInactive >= 14) return { opportunity: item, kind: 'inactive' as const, label: `${daysInactive} days without activity` };
      if (item.stage === 'proposal') return { opportunity: item, kind: 'proposal' as const, label: 'Proposal pending' };
      if (item.stage === 'meeting') return { opportunity: item, kind: 'meeting' as const, label: 'Upcoming meeting / follow-up' };
      return null;
    })
    .filter((item): item is NonNullable<typeof item> => Boolean(item))
    .sort((a, b) => (a.opportunity.nextActionDate || '9999').localeCompare(b.opportunity.nextActionDate || '9999'));
}

export function generateFitAnalysis(brand: SponsorBrand): string {
  const facts = [brand.industry, brand.category, brand.city, brand.country, ...brand.tags].filter(Boolean);
  const relationship = brand.relationshipType === 'cold_prospect' ? 'La relación todavía es fría' : 'Existe una relación previa aprovechable';
  return `${brand.priority === 'high' ? 'Alta' : brand.priority === 'medium' ? 'Media' : 'Baja'} afinidad según los datos cargados (${facts.slice(0, 4).join(', ')}). ${relationship}; validar audiencia, presupuesto e inventario antes de presentar una propuesta.`;
}

export function createSponsorId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export function normalizeSponsorOperationsState(value: unknown, legacySponsors: Sponsor[] = []): SponsorOperationsState {
  const fallback = EMPTY_SPONSOR_OPERATIONS;
  const hasStoredState = Boolean(value && typeof value === 'object');
  const candidate = hasStoredState ? value as Partial<SponsorOperationsState> : {};
  const arrayKeys: Array<keyof Pick<SponsorOperationsState, 'brands' | 'contacts' | 'opportunities' | 'inventory' | 'activations' | 'activities' | 'reports' | 'renewals' | 'objections'>> = [
    'brands', 'contacts', 'opportunities', 'inventory', 'activations', 'activities', 'reports', 'renewals', 'objections',
  ];
  const normalized = {
    ...fallback,
    ...candidate,
    version: 1,
    dataSource: hasStoredState ? candidate.dataSource || fallback.dataSource : legacySponsors.length > 0 ? 'migrated' : fallback.dataSource,
  } as SponsorOperationsState;
  for (const key of arrayKeys) {
    const source = Array.isArray(candidate[key]) ? candidate[key] : fallback[key];
    normalized[key] = [...source] as never;
  }

  const migrated = migrateLegacySponsors(legacySponsors);
  for (const brand of migrated.brands) if (!normalized.brands.some(item => item.id === brand.id)) normalized.brands.push(brand);
  for (const contact of migrated.contacts) if (!normalized.contacts.some(item => item.id === contact.id)) normalized.contacts.push(contact);
  for (const opportunity of migrated.opportunities) if (!normalized.opportunities.some(item => item.id === opportunity.id)) normalized.opportunities.push(opportunity);
  return normalized;
}

export function createRenewalOpportunity(source: SponsorOpportunity, strategy: NonNullable<SponsorOpportunity['renewalStrategy']>): SponsorOpportunity {
  const createdAt = new Date().toISOString();
  return {
    ...source,
    id: createSponsorId('sponsor-opportunity'),
    title: `${source.title} — Renewal`,
    stage: 'renewal',
    probability: 50,
    nextAction: 'Review renewal package with sponsor',
    nextActionDate: '',
    expectedCloseDate: '',
    startDate: '',
    endDate: '',
    objectionType: undefined,
    objectionNotes: undefined,
    previousOpportunityId: source.id,
    renewalStrategy: strategy,
    createdAt,
    updatedAt: createdAt,
  };
}

export function sumObservedMetrics(metricSets: SponsorObservedMetrics[]): SponsorObservedMetrics {
  const result: SponsorObservedMetrics = {};
  for (const metrics of metricSets) {
    for (const [key, value] of Object.entries(metrics)) {
      if (typeof value === 'number') result[key as keyof SponsorObservedMetrics] = (result[key as keyof SponsorObservedMetrics] || 0) + value;
    }
  }
  return result;
}

export function sumEstimatedMetrics(metricSets: SponsorEstimatedMetrics[]): SponsorEstimatedMetrics {
  const result: SponsorEstimatedMetrics = { methodology: metricSets.map(item => item.methodology).filter(Boolean).join(' · ') || 'Sin metodología cargada.' };
  for (const metrics of metricSets) {
    for (const key of ['estimatedReach', 'estimatedImpressions', 'footTraffic', 'estimatedExposure'] as const) {
      if (typeof metrics[key] === 'number') result[key] = (result[key] || 0) + metrics[key]!;
    }
  }
  return result;
}

export function generateSponsorReport(state: SponsorOperationsState, opportunity: SponsorOpportunity): SponsorReport {
  const activations = state.activations.filter(item => item.opportunityId === opportunity.id);
  return {
    id: createSponsorId('sponsor-report'),
    brandId: opportunity.brandId,
    opportunityId: opportunity.id,
    activationIds: activations.map(item => item.id),
    periodStart: opportunity.startDate || activations.map(item => item.date).sort()[0] || new Date().toISOString().slice(0, 10),
    periodEnd: opportunity.endDate || activations.map(item => item.date).sort().at(-1) || new Date().toISOString().slice(0, 10),
    objective: activations.map(item => item.objective).filter(Boolean).join(' · ') || 'Documentar resultados de la relación comercial.',
    locations: Array.from(new Set(activations.map(item => item.location).filter(Boolean))),
    inventoryIds: Array.from(new Set(activations.flatMap(item => item.inventoryIds))),
    observedMetrics: sumObservedMetrics(activations.map(item => item.observedMetrics)),
    estimatedMetrics: sumEstimatedMetrics(activations.map(item => item.estimatedMetrics)),
    assets: Array.from(new Set(activations.flatMap(item => item.assets))),
    insights: ['Completar con interpretación basada en métricas observadas.'],
    recommendations: ['Validar resultados con el sponsor antes de preparar la renovación.'],
    renewalProposal: 'Evaluar continuidad según inventario, resultados observados y objetivos del próximo período.',
    status: 'draft',
    createdAt: new Date().toISOString(),
  };
}
